// liftedBlacks — Huji "milky blacks": shadows never quite hit zero. Composites
// a warm brown overlay at 10% alpha in screen-blend mode, which raises the
// floor of the shadow region without much effect on midtones/highlights.
//
// Implemented per-pixel so it's testable in the synthetic ctx (which supports
// fillRect+screen but the math is identical here).

import {
  type FilterContext,
  clamp,
  eachPixel,
  getImageData,
  putImageData,
  screenBlend,
} from './_helpers'

const OVERLAY_R = 120
const OVERLAY_G = 90
const OVERLAY_B = 60
// F-wave β: 0.1 → 0.18 to lift shadows more aggressively. The contrast bump
// to 1.32 sinks shadows hard; lifting them back gives the milky-Polaroid look
// where blacks are genuinely brown, not crushed.
const OVERLAY_A = 0.18

export function liftedBlacks(c: FilterContext): void {
  const img = getImageData(c.ctx)
  eachPixel(img, (r, g, b, a) => {
    // Screen-blend the overlay over the base, then mix back at overlay alpha
    // so the global effect stays subtle (matches what ctx with
    // globalAlpha=0.1 + composite='screen' + fillRect would produce).
    const sr = screenBlend(r, OVERLAY_R)
    const sg = screenBlend(g, OVERLAY_G)
    const sb = screenBlend(b, OVERLAY_B)
    return [
      clamp(r + (sr - r) * OVERLAY_A),
      clamp(g + (sg - g) * OVERLAY_A),
      clamp(b + (sb - b) * OVERLAY_A),
      a,
    ]
  })
  putImageData(c.ctx, img)
}
